import { useState } from 'react';
import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  KeyRound,
  Leaf,
  Mail,
  MailCheck,
  MapPin,
  Route,
  Shield,
  ShieldCheck,
  Sparkles,
  User,
  Users,
} from 'lucide-react';
import { LoginFlowProps, StudentRole } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

type LoginStep =
  | 'user-type'
  | 'role-select'
  | 'email-form'
  | 'admin-form'
  | 'admin-email-sent'
  | 'submitting';
type UserType = 'user' | 'admin' | null;

const SUPER_ADMIN_EMAIL = 'joeslamie@gmail.com';

const loginHighlights = [
  {
    icon: Route,
    title: 'Campus mobility access',
    description: 'Enter a system designed to coordinate practical student transportation across everyday and urgent ride needs.',
  },
  {
    icon: Users,
    title: 'Student-centered network',
    description: 'Ride activity is organized around verified students helping fellow students within their campus community.',
  },
  {
    icon: ShieldCheck,
    title: 'Structured with trust',
    description: 'Verification, platform policy, and shared responsibility help keep the experience clearer and safer.',
  },
];

const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const LoginFlow = ({ onLoginSuccess, onAdminLogin, onRegisterClick }: LoginFlowProps) => {
  const [step, setStep] = useState<LoginStep>('user-type');
  const [userType, setUserType] = useState<UserType>(null);
  const [selectedRole, setSelectedRole] = useState<StudentRole | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [adminMagicLinkMessage, setAdminMagicLinkMessage] = useState('Check your email for the admin sign-in link.');

  const trimmedEmail = email.trim();
  const trimmedPassword = password.trim();
  const isSuperAdminLogin = userType === 'admin' && trimmedEmail.toLowerCase() === SUPER_ADMIN_EMAIL;

  const resetFormState = () => {
    setEmail('');
    setPassword('');
    setSelectedRole(null);
    setAdminMagicLinkMessage('Check your email for the admin sign-in link.');
  };

  const handleUserTypeSelect = (type: 'user' | 'admin') => {
    setUserType(type);
    resetFormState();
    setStep(type === 'admin' ? 'admin-form' : 'role-select');
  };

  const handleRoleSelect = (role: StudentRole) => {
    setSelectedRole(role);
    setEmail('');
    setStep('email-form');
  };

  const handleStudentLoginSubmit = async () => {
    if (!validateEmail(trimmedEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!selectedRole) return;

    setStep('submitting');
    try {
      await onLoginSuccess(trimmedEmail, selectedRole);
    } finally {
      setStep('email-form');
    }
  };

  const handleAdminSubmit = async () => {
    if (!validateEmail(trimmedEmail)) {
      toast.error('Please enter a valid admin email address');
      return;
    }

    if (isSuperAdminLogin && !trimmedPassword) {
      toast.error('Please enter the super admin password');
      return;
    }

    setStep('submitting');
    try {
      const result = await onAdminLogin(trimmedEmail, trimmedPassword || undefined);
      if (result.mode === 'magic-link') {
        setAdminMagicLinkMessage(result.message || 'Check your email for the admin sign-in link.');
        setStep('admin-email-sent');
        return;
      }
      setStep('admin-form');
    } catch {
      setStep('admin-form');
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <div className="absolute inset-x-0 top-0 h-72 bg-[radial-gradient(circle_at_top_left,_hsla(25,85%,55%,0.18),_transparent_42%),radial-gradient(circle_at_top_right,_hsla(140,35%,45%,0.12),_transparent_36%)]" />
      <div className="absolute left-[-4rem] top-24 h-40 w-40 rounded-full bg-primary/10 blur-3xl" />
      <div className="absolute right-[-3rem] top-10 h-48 w-48 rounded-full bg-accent/10 blur-3xl" />

      <main className="relative mx-auto flex min-h-screen w-full max-w-6xl items-center px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid w-full gap-8 lg:grid-cols-[1.08fr_0.92fr] lg:gap-10">
          <section className="flex flex-col justify-center">
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-primary/15 bg-card/80 px-4 py-2 text-sm font-medium text-foreground shadow-warm backdrop-blur">
              <div className="rounded-full gradient-warm p-1.5 shadow-warm">
                <Leaf className="h-4 w-4 text-primary-foreground" />
              </div>
              CollegeRyde
            </div>

            <div className="mt-6 max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">Welcome back</p>
              <h1 className="mt-4 font-display text-4xl font-extrabold leading-tight text-foreground sm:text-5xl">
                Return to the student mobility network built for campus reliability.
              </h1>
              <p className="mt-5 max-w-xl text-base leading-7 text-muted-foreground sm:text-lg">
                CollegeRyde helps campus communities coordinate rides for daily essentials, urgent situations, and stronger peer connection through one modular platform.
              </p>
            </div>

            <div className="mt-8 grid gap-4">
              {loginHighlights.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="flex gap-4 rounded-3xl border border-border/70 bg-card/85 p-5 shadow-warm backdrop-blur">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl gradient-warm shadow-warm">
                      <Icon className="h-5 w-5 text-primary-foreground" />
                    </div>
                    <div>
                      <h2 className="font-display text-lg font-bold text-foreground">{item.title}</h2>
                      <p className="mt-1 text-sm leading-6 text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 flex flex-wrap gap-3 text-sm text-muted-foreground">
              <div className="inline-flex items-center gap-2 rounded-full bg-muted/70 px-3 py-2">
                <MapPin className="h-4 w-4 text-primary" />
                Campus-based access
              </div>
              <div className="inline-flex items-center gap-2 rounded-full bg-muted/70 px-3 py-2">
                <Clock3 className="h-4 w-4 text-primary" />
                Fast magic-link entry
              </div>
            </div>
          </section>

          <section className="w-full">
            <div className="rounded-[2rem] border border-border/70 bg-card/95 p-6 shadow-warm-lg backdrop-blur sm:p-8">
              {step === 'user-type' && (
                <div className="animate-fade-in space-y-6">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-secondary/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-secondary-foreground">
                      Account access
                    </div>
                    <h2 className="mt-4 font-display text-3xl font-bold text-foreground">Welcome back</h2>
                    <p className="mt-3 text-sm leading-6 text-muted-foreground">
                      Select your access type to continue into the CollegeRyde platform.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <button
                      onClick={() => handleUserTypeSelect('user')}
                      className={cn(
                        'flex w-full items-center gap-4 rounded-3xl border border-border bg-background/70 p-5 text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-warm-lg',
                      )}
                    >
                      <div className="rounded-2xl gradient-warm p-3 shadow-warm">
                        <User className="h-6 w-6 text-primary-foreground" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-display text-lg font-bold text-foreground">Student User</h3>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                          Continue as a rider or driver to access CollegeRyde mobility services.
                        </p>
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground" />
                    </button>

                    <button
                      onClick={() => handleUserTypeSelect('admin')}
                      className={cn(
                        'flex w-full items-center gap-4 rounded-3xl border border-border bg-background/70 p-5 text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-warm-lg',
                      )}
                    >
                      <div className="rounded-2xl bg-primary/10 p-3 shadow-warm">
                        <Shield className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-display text-lg font-bold text-foreground">Admin Portal</h3>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                          Continue as a school admin or super admin to access moderation tools.
                        </p>
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground" />
                    </button>
                  </div>

                  <button
                    onClick={onRegisterClick}
                    className="w-full text-center text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    New to CollegeRyde? <span className="font-medium text-primary">Create account</span>
                  </button>
                </div>
              )}

              {step === 'role-select' && (
                <div className="animate-fade-in space-y-6">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-secondary/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-secondary-foreground">
                      Select role
                    </div>
                    <h2 className="mt-4 font-display text-3xl font-bold text-foreground">How will you use CollegeRyde today?</h2>
                    <p className="mt-3 text-sm leading-6 text-muted-foreground">
                      Choose the side of the mobility network you want to access right now.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <button
                      onClick={() => handleRoleSelect('driver')}
                      className={cn(
                        'flex w-full items-start gap-4 rounded-3xl border border-border bg-background/70 p-5 text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-warm-lg',
                      )}
                    >
                      <div className="rounded-2xl gradient-warm p-3 shadow-warm">
                        <User className="h-6 w-6 text-primary-foreground" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-display text-lg font-bold text-foreground">Driver</h3>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                          Log in to manage rides you offer to the campus community.
                        </p>
                      </div>
                      <ArrowRight className="mt-1 h-5 w-5 text-muted-foreground" />
                    </button>

                    <button
                      onClick={() => handleRoleSelect('rider')}
                      className={cn(
                        'flex w-full items-start gap-4 rounded-3xl border border-border bg-background/70 p-5 text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-accent/40 hover:shadow-warm-lg',
                      )}
                    >
                      <div className="rounded-2xl bg-accent/20 p-3 shadow-warm">
                        <User className="h-6 w-6 text-accent" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-display text-lg font-bold text-foreground">Rider</h3>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                          Log in to browse available student ride options.
                        </p>
                      </div>
                      <ArrowRight className="mt-1 h-5 w-5 text-muted-foreground" />
                    </button>
                  </div>

                  <button
                    onClick={() => setStep('user-type')}
                    className="w-full py-2 text-center text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    ← Back to account type
                  </button>
                </div>
              )}

              {(step === 'email-form' || (step === 'submitting' && userType === 'user')) && selectedRole && (
                <div className="animate-fade-in space-y-6">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-secondary/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-secondary-foreground">
                      Magic link login
                    </div>
                    <h2 className="mt-4 font-display text-3xl font-bold text-foreground">
                      Log in as {selectedRole === 'driver' ? 'Driver' : 'Rider'}
                    </h2>
                    <p className="mt-3 text-sm leading-6 text-muted-foreground">
                      Enter your email to receive a secure sign-in link.
                    </p>
                  </div>

                  <div className="rounded-3xl border border-border/70 bg-background/70 p-5 shadow-warm">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">Email Address *</label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          type="email"
                          placeholder="student@university.edu"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="h-12 rounded-xl pl-10"
                          autoFocus
                          disabled={step === 'submitting'}
                        />
                      </div>
                    </div>

                    <Button
                      onClick={handleStudentLoginSubmit}
                      disabled={step === 'submitting' || !trimmedEmail}
                      className="mt-5 h-14 w-full rounded-xl gradient-warm text-lg transition-opacity hover:opacity-90"
                    >
                      {step === 'submitting' ? (
                        <>
                          <span className="mr-2 animate-spin">⏳</span>
                          Sending Magic Link...
                        </>
                      ) : (
                        <>
                          Send Magic Link
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>
                  </div>

                  <button
                    onClick={() => setStep('role-select')}
                    className="w-full text-center text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    ← Change role
                  </button>
                </div>
              )}

              {(step === 'admin-form' || (step === 'submitting' && userType === 'admin')) && (
                <div className="animate-fade-in space-y-6">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-secondary/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-secondary-foreground">
                      Admin access
                    </div>
                    <h2 className="mt-4 font-display text-3xl font-bold text-foreground">Enter the admin portal</h2>
                    <p className="mt-3 text-sm leading-6 text-muted-foreground">
                      School admins can sign in with email only for a magic link or use email plus password for direct login. Super admin access still requires a password.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="rounded-[1.75rem] border border-primary/15 bg-[linear-gradient(135deg,hsla(25,85%,55%,0.08),hsla(140,35%,45%,0.08))] p-5">
                      <div className="flex items-start gap-3">
                        <div className="rounded-2xl bg-card/90 p-3 text-primary shadow-warm">
                          <Sparkles className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="font-display text-lg font-bold text-foreground">Flexible admin sign-in</p>
                          <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            Leave password empty to receive a magic link. Add a password to attempt direct school-admin or super-admin authentication.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-3xl border border-border/70 bg-background/70 p-5 shadow-warm">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-foreground">Admin Email *</label>
                          <div className="relative">
                            <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              type="email"
                              placeholder="admin@school.edu"
                              value={email}
                              onChange={(e) => {
                                setEmail(e.target.value);
                                if (step === 'admin-email-sent') setStep('admin-form');
                              }}
                              className="h-12 rounded-xl pl-10"
                              autoFocus
                              disabled={step === 'submitting'}
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-3">
                            <label className="text-sm font-medium text-foreground">
                              {isSuperAdminLogin ? 'Super Admin Password *' : 'Password'}
                            </label>
                            {!isSuperAdminLogin && (
                              <span className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Optional</span>
                            )}
                          </div>
                          <div className="relative">
                            <KeyRound className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              type="password"
                              placeholder={isSuperAdminLogin ? 'Enter password' : 'Leave empty for magic link'}
                              value={password}
                              onChange={(e) => {
                                setPassword(e.target.value);
                                if (step === 'admin-email-sent') setStep('admin-form');
                              }}
                              className="h-12 rounded-xl pl-10"
                              disabled={step === 'submitting'}
                            />
                          </div>
                          <p className="text-xs leading-5 text-muted-foreground">
                            {isSuperAdminLogin
                              ? `Super admin login is enabled for ${SUPER_ADMIN_EMAIL}.`
                              : 'Password triggers direct login. No password sends a magic link instead.'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-3 rounded-3xl border border-border/70 bg-card/70 p-4 text-sm text-muted-foreground">
                      <div className="flex items-start gap-3">
                        <MailCheck className="mt-0.5 h-4 w-4 text-primary" />
                        <p>Email only sends a magic link for school admin access.</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <KeyRound className="mt-0.5 h-4 w-4 text-primary" />
                        <p>Email plus password attempts direct login and routes straight to the dashboard on success.</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <ShieldCheck className="mt-0.5 h-4 w-4 text-primary" />
                        <p>Super admin still requires both email and password.</p>
                      </div>
                    </div>

                    <Button
                      onClick={handleAdminSubmit}
                      disabled={step === 'submitting' || !trimmedEmail || (isSuperAdminLogin && !trimmedPassword)}
                      className="h-14 w-full rounded-xl gradient-warm text-lg transition-opacity hover:opacity-90"
                    >
                      {step === 'submitting' ? (
                        <>
                          <span className="mr-2 animate-spin">⏳</span>
                          {trimmedPassword ? 'Signing In...' : 'Sending Magic Link...'}
                        </>
                      ) : (
                        <>
                          {trimmedPassword ? 'Continue to Admin Dashboard' : 'Send Admin Magic Link'}
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>

                    <button
                      onClick={() => setStep('user-type')}
                      className="w-full py-2 text-center text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      ← Back to account type
                    </button>
                  </div>
                </div>
              )}

              {step === 'admin-email-sent' && (
                <div className="animate-fade-in space-y-6">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary shadow-warm">
                    <CheckCircle2 className="h-8 w-8" />
                  </div>

                  <div className="text-center">
                    <div className="inline-flex items-center gap-2 rounded-full bg-secondary/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-secondary-foreground">
                      Check your email
                    </div>
                    <h2 className="mt-4 font-display text-3xl font-bold text-foreground">Admin magic link sent</h2>
                    <p className="mt-3 text-sm leading-6 text-muted-foreground">
                      {adminMagicLinkMessage}
                    </p>
                  </div>

                  <div className="rounded-3xl border border-border/70 bg-background/70 p-5 text-sm text-muted-foreground shadow-warm">
                    <p className="font-medium text-foreground">{trimmedEmail}</p>
                    <p className="mt-2 leading-6">
                      Open the secure link from your inbox to finish signing in. If you meant to use a password instead, return to the form and add it before submitting.
                    </p>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <Button
                      variant="outline"
                      onClick={() => setStep('admin-form')}
                      className="h-12 rounded-xl"
                    >
                      Edit sign-in details
                    </Button>
                    <Button
                      onClick={handleAdminSubmit}
                      disabled={!trimmedEmail}
                      className="h-12 rounded-xl gradient-warm transition-opacity hover:opacity-90"
                    >
                      Resend link
                    </Button>
                  </div>

                  <button
                    onClick={() => setStep('user-type')}
                    className="w-full py-2 text-center text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    ← Back to account type
                  </button>
                </div>
              )}

              <div className="mt-8 border-t border-border/70 pt-5 text-center">
                <p className="text-xs text-muted-foreground">
                  Magic links expire in 15 minutes for your security
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default LoginFlow;
